/**
 * Jotai atoms for authentication state
 */

import { atom } from 'jotai';
import { syncEnabledModulesCache } from './permissions';
import { getAuthCookie, setAuthCookie, clearAuthCookie } from './session-cookie';
import { decodeAccessTokenPayload } from './jwt';
import {
  clearUserBootstrapSnapshot,
  consumeSessionWarmed,
  readUserBootstrapSnapshot,
} from './session-warm';
import type { JwtPayload, TokenDto } from '../api/types';
import type { User } from '../api/users';

// Token storage atoms — backed by a cookie shared across the Lion Pro
// ecosystem's apps (see session-cookie.ts), not per-origin localStorage.
export const accessTokenAtom = atom<string | null>(
  typeof window !== 'undefined' ? getAuthCookie('access_token') : null,
);

export const refreshTokenAtom = atom<string | null>(
  typeof window !== 'undefined' ? getAuthCookie('refresh_token') : null,
);

export const refreshTokenExpiresAtAtom = atom<string | null>(
  typeof window !== 'undefined'
    ? getAuthCookie('refresh_token_expires_at')
    : null,
);

function bootstrapUserFromCookie(): User | null {
  if (typeof window === 'undefined') return null;
  const snap = readUserBootstrapSnapshot();
  if (!snap) return null;
  // Minimal User so AppShell can decide profile-setup without waiting on /users/me.
  return {
    id: snap.id,
    email: snap.email,
    invitedAs: null,
    firstName: snap.firstName,
    lastName: snap.lastName,
    avatarUrl: null,
    googleId: null,
    googleAvatarUrl: null,
    emailVerified: true,
    phoneVerified: false,
    profileSetupCompleted: snap.profileSetupCompleted,
    preferredLanguage: snap.preferredLanguage,
    preferredTimezone: snap.preferredTimezone,
    organizations: [],
    pendingOrganizations: [],
    pendingOrganizationInvites: [],
    hasOrganizationMembership: false,
    hasPendingOrganizationMembership: false,
    hasProductAccess: false,
    roles: [],
    globalRoles: [],
    permissions: [],
    navigation: { modules: [] },
    createdAt: '',
    updatedAt: '',
  };
}

// User data atoms — payload hydrates from local JWT so isAuthenticated is true on first paint.
export const userAtom = atom<User | null>(bootstrapUserFromCookie());
export const userPayloadAtom = atom<JwtPayload | null>(
  typeof window !== 'undefined'
    ? decodeAccessTokenPayload(getAuthCookie('access_token'))
    : null,
);

// Derived atoms
export const isAuthenticatedAtom = atom((get) => {
  const accessToken = get(accessTokenAtom);
  const userPayload = get(userPayloadAtom);
  return !!accessToken && !!userPayload;
});

/** True once background session hydrate (/users/me) has settled. */
export const userHydratedAtom = atom(false);

// Helper atom to sync tokens to the shared cookie
export const syncTokensAtom = atom(
  null,
  (get, set, tokens: TokenDto | null) => {
    if (tokens) {
      set(accessTokenAtom, tokens.access_token);
      set(refreshTokenAtom, tokens.refresh_token);
      set(refreshTokenExpiresAtAtom, tokens.refresh_token_expires_at);

      if (typeof window !== 'undefined') {
        setAuthCookie('access_token', tokens.access_token);
        setAuthCookie('refresh_token', tokens.refresh_token);
        setAuthCookie(
          'refresh_token_expires_at',
          tokens.refresh_token_expires_at,
        );
      }
    } else {
      set(accessTokenAtom, null);
      set(refreshTokenAtom, null);
      set(refreshTokenExpiresAtAtom, null);

      if (typeof window !== 'undefined') {
        clearAuthCookie('access_token');
        clearAuthCookie('refresh_token');
        clearAuthCookie('refresh_token_expires_at');
      }
    }
  },
);

// Helper atom to clear all auth state
export const clearAuthAtom = atom(null, (get, set) => {
  set(syncTokensAtom, null);
  set(userAtom, null);
  set(userPayloadAtom, null);
  set(userHydratedAtom, false);
  if (typeof window !== 'undefined') {
    clearUserBootstrapSnapshot();
    consumeSessionWarmed();
  }
  syncEnabledModulesCache([]);
});
